import { useEffect, useState } from 'react';

export enum PaginationType {
  DOUBLE_PREVIOUS = 'DOUBLE_PREVIOUS',
  PREVIOUS = 'PREVIOUS',
  NEXT = 'NEXT',
  DOUBLE_NEXT = 'DOUBLE_NEXT',
  LINK = 'LINK',
}

export interface PaginationInfo {
  type: PaginationType;
  page: number;
  isActive: boolean;
}

const getPageInfos = (page: number, totalPage: number, perPage: number) => {
  const pageInfo: PaginationInfo[] = [];

  const halfPerPage = Math.floor(perPage / 2);
  let startPage = page - halfPerPage;
  let endPage = page + halfPerPage;

  if (startPage < 1) {
    startPage = 1;
    endPage = Math.min(perPage, totalPage);
  } else if (endPage > totalPage) {
    endPage = totalPage;
    startPage = Math.max(1, totalPage - perPage + 1);
  }

  const doublePreviousPage = Math.max(1, page - perPage);
  pageInfo.push({
    type: PaginationType.DOUBLE_PREVIOUS,
    page: doublePreviousPage,
    isActive: page > 1,
  });

  const previousPage = Math.max(1, page - 1);
  pageInfo.push({
    type: PaginationType.PREVIOUS,
    page: previousPage,
    isActive: page > 1,
  });

  for (let i = startPage; i <= endPage; i++) {
    pageInfo.push({
      type: PaginationType.LINK,
      page: i,
      isActive: i === page,
    });
  }

  const nextPage = Math.min(totalPage, page + 1);
  pageInfo.push({
    type: PaginationType.NEXT,
    page: nextPage,
    isActive: page < totalPage,
  });

  const doubleNextPage = Math.min(totalPage, page + perPage);
  pageInfo.push({
    type: PaginationType.DOUBLE_NEXT,
    page: doubleNextPage,
    isActive: page < totalPage,
  });

  return pageInfo;
};

const usePagination = (page: number, totalPage: number, perPage = 5) => {
  const [pageInfos, setPageInfos] = useState<PaginationInfo[]>(() => getPageInfos(page, totalPage, perPage));
  useEffect(() => {
    setPageInfos(getPageInfos(page, totalPage, perPage));
  }, [page, totalPage, perPage]);

  return pageInfos;
};

export default usePagination;
